import cls from "@fvilers/cls";
import { CloseIcon } from "@imi/icons/CloseIcon.js";
import { ToastIcon } from "@imi/icons/ToastIcon.js";
import { ComponentPropsWithRef, useEffect, useRef } from "react";
import { TouchTarget } from "../TouchTarget/TouchTarget";
import styles from "./Toast.module.css";

type ToastProps = {
  duration?: number;
  show?: boolean;
} & ComponentPropsWithRef<"div">;

export function Toast({
  children,
  className,
  duration,
  show,
  ...props
}: ToastProps) {
  return (
    <aside
      {...props}
      className={cls(styles.Toast, show && styles.show, className)}
      data-slot="toast"
      style={
        {
          "--toast-duration": `${(duration ?? 5000) - 400}ms`,
        } as React.CSSProperties
      }
    >
      <ToastIcon />
      {children}
    </aside>
  );
}

export function ToastCloseButton({
  className,
  ...props
}: ComponentPropsWithRef<"button">) {
  return (
    <button
      {...props}
      className={cls(styles.ToastCloseButton, className)}
      data-slot="toast-close-button"
    >
      <TouchTarget>
        <CloseIcon />
      </TouchTarget>
    </button>
  );
}

export function ToastContent({
  className,
  ...props
}: ComponentPropsWithRef<"div">) {
  return (
    <div
      {...props}
      className={cls(styles.ToastContent, className)}
      data-slot="toast-content"
    />
  );
}

export function ToastDescription({
  className,
  ...props
}: ComponentPropsWithRef<"p">) {
  return (
    <p
      className={cls(styles.ToastDescription, className)}
      {...props}
      data-slot="description"
    />
  );
}

export function ToastRegion({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<"div">) {
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.showPopover();
    return () => {
      if (el.matches(":popover-open")) el.hidePopover();
    };
  }, []);

  return (
    <div
      {...props}
      className={cls(styles.ToastRegion, className)}
      data-slot="toast-region"
      popover="manual"
      ref={(node) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
    />
  );
}

export function ToastTitle({
  className,
  ...props
}: ComponentPropsWithRef<"h1">) {
  return (
    <h1
      className={cls(styles.ToastTitle, className)}
      {...props}
      data-slot="title"
    />
  );
}
